/**
 * Hook for managing caption data — handles preloaded cues and AJAX fetching.
 */

import { useState, useEffect, useCallback } from "react";
import type { SubtitlesConfig, Cue } from "../definitions/types";
import { detectBrowserLang } from "../utils/captions";

interface CaptionsState {
  currentLang: string;
  cues: Cue[];
  loading: boolean;
  switchLang: (lang: string) => void;
}

export default function useCaptions(config: SubtitlesConfig): CaptionsState {
  const { languages, defaultLang, langFromUrl, defaultCues, ajaxUrl, videoId } = config;

  // A language requested in the URL wins over the browser's preference.
  const initialLang = langFromUrl ? defaultLang : detectBrowserLang(languages, defaultLang);
  const hasPreloadedCues =
    initialLang === defaultLang && defaultCues.length > 0;

  const [currentLang, setCurrentLang] = useState(initialLang);
  const [cuesMap, setCuesMap] = useState<Record<string, Cue[]>>(
    hasPreloadedCues ? { [initialLang]: defaultCues } : {},
  );
  const [loading, setLoading] = useState(!hasPreloadedCues);

  // Fetch cues for a language from AJAX endpoint
  const fetchCues = useCallback(
    (lang: string) => {
      setLoading(true);
      fetch(
        `${ajaxUrl}?action=kft_yt_captions&video_id=${videoId}&lang=${lang}`,
      )
        .then((r) => r.json())
        .then((res: { success: boolean; data: Cue[] }) => {
          if (res.success && res.data?.length) {
            setCuesMap((prev) => ({ ...prev, [lang]: res.data }));
          }
        })
        .finally(() => setLoading(false));
    },
    [ajaxUrl, videoId],
  );

  // If browser lang differs from preloaded default, fetch on mount
  useEffect(() => {
    if (!hasPreloadedCues) {
      fetchCues(initialLang);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const switchLang = useCallback(
    (lang: string) => {
      setCurrentLang(lang);
      if (!cuesMap[lang]) {
        fetchCues(lang);
      }
    },
    [cuesMap, fetchCues],
  );

  return {
    currentLang,
    cues: cuesMap[currentLang] ?? [],
    loading,
    switchLang,
  };
}
